import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { purple } from '@mui/material/colors';
import {
  Container,
  useMediaQuery,
  Typography,
  Box,
  Paper,
  FormControl,
  TextField,
  Grid,
  Button,
  Divider,
} from '@mui/material';
import { Reddit } from '@mui/icons-material';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useFirebase, useFirestore } from 'react-redux-firebase';
import axios from 'axios';
import { actions } from '../../actions';
import { useDispatch, useSelector } from 'react-redux';
import { toUpper, trim } from 'lodash';
import EntitiesHandler from './components/EntitiesHandler';
import IntentsHandler from './components/IntentsHandler';
import collections from '../../constants/FirestoreCollections';

const MySwal = withReactContent(Swal);
const urlDialogflow = process.env.REACT_APP_DIALOGFLOW;

const useStyles = (small) => {
  return makeStyles((theme) => ({
    appBarSpacer: theme.mixins.toolbar,
    training: {
      paddingBlock: 32,
    },
    title: {
      fontSize: theme.sizes.md.t3,
    },
    caseItem: {
      padding: 16,
    },
    specialtyItem: {
      padding: 16,
      display: 'flex',
      alignItems: 'center',
    },
    sectionRef: {
      fontWeight: 'bold',
    },
    synonymTag: {
      marginRight: 8,
      backgroundColor: purple[500],
      '&:hover': {
        backgroundColor: purple[300],
      },
      cursor: 'pointer',
      color: 'white',
      borderRadius: 4,
      padding: 4,
    },
    actionsButtons: {
      background: theme.palette.secondary.main,
      color: 'white',
      '&:hover': {
        background: theme.palette.secondary.light,
      },
    },
  }));
};

export default function Training() {
  const small = useMediaQuery('(max-width:600px)');
  const classes = useStyles(small)();
  const organization = useSelector((state) => state.app.organization);
  const profile = useSelector((state) => state.firebase.profile);
  const firestore = useFirestore();
  const firebase = useFirebase();
  const appDispatch = useDispatch();

  const [trainingConfigId, setTrainingConfigId] = useState();
  const [unhandledProfessionals, setUnhandledProfessionals] = useState([]);

  useEffect(() => {
    if (organization.id) {
      firestore
        .collection(collections.configurations)
        .where('name', '==', 'professionalsExtraCases')
        .where('organizationId', '==', organization.id)
        .get()
        .then((res) => {
          if (!res.empty) {
            const doc = res.docs[0].data();
            if (doc.professionalsExtraCases) setUnhandledProfessionals(doc.professionalsExtraCases);
            else setUnhandledProfessionals([]);
            setTrainingConfigId(res.docs[0].id);
          }
        });
    }
  }, [organization.id]);

  const handleTrain = async () => {
    const unhandledProfessionalsToAdd = unhandledProfessionals
      .filter((e) => e.name && e.surname)
      .map((e) => ({ ...e, name: trim(e.name), surname: trim(e.surname) }));

    if (!unhandledProfessionalsToAdd || unhandledProfessionalsToAdd.length === 0) {
      MySwal.fire({
        text: 'No hay ningún caso que agregar',
        showConfirmButton: false,
        icon: 'info',
        timer: 1500,
      });
      return;
    }

    appDispatch({
      type: actions.changeLoading,
      loading: { loading: true, priority: 1 },
    });

    try {
      const token = await firebase.auth().currentUser.getIdToken(true);

      const res = await axios(`${urlDialogflow}/entities`, {
        method: 'POST',
        headers: {
          Authorization: `bearer ${token}`,
        },
        data: {
          organizationId: organization.id,
          unhandledCases: unhandledProfessionalsToAdd,
          profile: profile?.email,
        },
      });

      if (res.status === 200) {
        let allCases = unhandledProfessionalsToAdd.map((e) => ({ ...e, solved: true }));
        allCases = [
          ...allCases,
          ...unhandledProfessionals.filter(
            (orgCase) => !allCases.find((e) => toUpper(e.value) === toUpper(orgCase.value)),
          ),
        ];

        await firestore.collection(collections.configurations).doc(trainingConfigId).update({
          professionalsExtraCases: allCases,
        });

        setUnhandledProfessionals(allCases);

        MySwal.fire({
          text: 'Entrenado exitosamente',
          showConfirmButton: false,
          icon: 'success',
          timer: 1500,
        });
      }
    } catch (err) {
      console.error('Error during training:', err);
      MySwal.fire({
        text: 'Ocurrió un error durante el entrenamiento',
        showConfirmButton: true,
        icon: 'error',
      });
    } finally {
      appDispatch({
        type: actions.changeLoading,
        loading: { loading: false, priority: 1 },
      });
    }
  };

  const updateAgent = (type) => {
    MySwal.fire({
      text: '¿Esta seguro que quiere continuar?',
      showConfirmButton: true,
      showDenyButton: true,
      icon: 'error',
      inputValue: true,
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          appDispatch({
            type: actions.changeLoading,
            loading: {
              loading: true,
              priority: 1,
              text: `cargando`,
            },
          });
          await axios(`${urlDialogflow}/organization`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            data: {
              organizationId: organization.id,
              type,
              updateConfig: true,
              profile: profile?.email,
            },
          })
            .then(() => {
              appDispatch({
                type: actions.alert,
                alert: {
                  snackbarOpen: true,
                  snackbarMessage: `¡Operación completada correctamente!`,
                  snackbarSeverity: 'success',
                },
              });
            })
            .catch((error) => {
              const response = error.response && error.response.data;
              if (response && response.code === 'API_NOT_FOUND') {
                const label = type === 0 ? 'profesionales' : 'datos';
                appDispatch({
                  type: actions.alert,
                  alert: {
                    snackbarOpen: true,
                    snackbarMessage: `No se encontraron ${label} en la API`,
                    snackbarSeverity: 'error',
                  },
                });
              } else {
                appDispatch({
                  type: actions.alert,
                  alert: {
                    snackbarOpen: true,
                    snackbarMessage: `Ocurrio un error, intente mas tarde`,
                    snackbarSeverity: 'error',
                  },
                });
              }
            });
        }
      })
      .catch((error) => {
        console.error('updateAgent', error);
        const errorMessage =
          error.response.data.message || 'Ocurrió un error :( Por favor intente más tarde.';

        appDispatch({
          type: actions.alert,
          alert: {
            snackbarOpen: true,
            snackbarMessage: errorMessage,
            snackbarSeverity: 'error',
          },
        });
      })
      .finally(() => {
        appDispatch({
          type: actions.changeLoading,
          loading: { loading: false, priority: 1, text: '' },
        });
      });
  };

  const updateTrainingPhrases = () => {
    MySwal.fire({
      html: 'Esta acción reemplazará los valores de entidades de salud en TODAS las frases de entrenamiento del agente y <strong>no se puede revertir.</strong>',
      title: '¿Está seguro/a que desea continuar?',
      showConfirmButton: true,
      confirmButtonText: 'Actualizar',
      cancelButtonText: 'Cancelar',
      showCancelButton: true,
      showCloseButton: true,
      icon: 'warning',
      inputValue: true,
    }).then((result) => {
      if (result.isConfirmed) {
        appDispatch({
          type: actions.changeLoading,
          loading: {
            loading: true,
            priority: 1,
            text: `cargando`,
          },
        });
        axios(
          `${urlDialogflow}/update-all-training-phrases?agentId=${organization.provider.dfId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
          .then(() => {
            appDispatch({
              type: actions.alert,
              alert: {
                snackbarOpen: true,
                snackbarMessage: `Las frases fueron actualizadas correctamente :)`,
                snackbarSeverity: 'success',
              },
            });
          })
          .catch((error) => {
            let errorMessage = 'Ocurrió un error :( Por favor intente más tarde.';
            if (error.response.status === 400) errorMessage = error.response.data;
            appDispatch({
              type: actions.alert,
              alert: {
                snackbarOpen: true,
                snackbarMessage: errorMessage,
                snackbarSeverity: 'error',
              },
            });
          })
          .finally(() => {
            appDispatch({
              type: actions.changeLoading,
              loading: { loading: false, priority: 1, text: '' },
            });
          });
      }
    });
  };

  const hasProfessionalsForTraining =
    unhandledProfessionals && unhandledProfessionals.filter((e) => !e.solved).length;
  return (
    <div>
      <div className={classes.appBarSpacer} />
      <Container className={classes.training} maxWidth="lg">
        <Grid container display="flex" justifyContent="center">
          <Grid item sm={10}>
            <Box my={2}>
              <Box pb={2} display="flex" alignItems="center">
                <Box pr={1}>
                  <Reddit />
                </Box>
                <Typography className={classes.title}>Entrenamiento</Typography>
              </Box>
              {organization && organization.services && organization.services.healthSoftware && (
                <>
                  <Box mt={2} mb={2}>
                    <Typography className={classes.sectionRef}>ACCIONES</Typography>
                  </Box>
                  <Box mb={2} display="flex" alignItems="center">
                    <Box mr={2}>
                      <Button
                        className={classes.actionsButtons}
                        variant="contained"
                        color="primary"
                        onClick={() => updateAgent(0)}
                      >
                        Actualizar profesionales habilitados
                      </Button>
                    </Box>
                    <Box mr={2}>
                      <Button
                        className={classes.actionsButtons}
                        variant="contained"
                        color="primary"
                        onClick={() => updateAgent(2)}
                      >
                        Actualizar obras sociales
                      </Button>
                    </Box>
                  </Box>
                  <Box mt={2} mb={2}>
                    <Typography className={classes.sectionRef}>ONBOARDING</Typography>
                  </Box>
                  <Box mb={2} display="flex" alignItems="center">
                    <Box mr={2}>
                      <Button
                        className={classes.actionsButtons}
                        variant="contained"
                        color="info"
                        onClick={updateTrainingPhrases}
                      >
                        Actualizar frases de entrenamiento
                      </Button>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 4 }} />
                </>
              )}
              {unhandledProfessionals && unhandledProfessionals.length > 0 && (
                <Box>
                  {!!hasProfessionalsForTraining && (
                    <Box>
                      <Typography className={classes.sectionRef}>PROFESIONALES</Typography>
                      <Box my={2}>
                        <Button variant="outlined" color="primary" onClick={handleTrain}>
                          Entrenar
                        </Button>
                      </Box>
                    </Box>
                  )}
                  {unhandledProfessionals.map((extraCase, index) => {
                    return extraCase.solved ? null : (
                      <Box key={extraCase.value} my={2}>
                        <Paper className={classes.caseItem}>
                          <Typography>{extraCase.value}</Typography>
                          <Grid container>
                            <Grid item sm={4}>
                              <FormControl fullWidth>
                                <TextField
                                  label="Apellido"
                                  name="surname"
                                  required
                                  value={extraCase.surname}
                                  onChange={(e) => {
                                    setUnhandledProfessionals(
                                      unhandledProfessionals.map((item) =>
                                        extraCase.value !== item.value
                                          ? item
                                          : { ...item, surname: e.target.value },
                                      ),
                                    );
                                  }}
                                />
                              </FormControl>
                            </Grid>
                            <Grid item sm={4}>
                              <Box ml={2}>
                                <FormControl fullWidth>
                                  <TextField
                                    label="Nombre"
                                    name="name"
                                    required
                                    value={extraCase.name}
                                    onChange={(e) => {
                                      setUnhandledProfessionals(
                                        unhandledProfessionals.map((item) =>
                                          extraCase.value !== item.value
                                            ? item
                                            : { ...item, name: e.target.value },
                                        ),
                                      );
                                    }}
                                  />
                                </FormControl>
                              </Box>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Box>
                    );
                  })}
                </Box>
              )}
              {!!hasProfessionalsForTraining && <Divider sx={{ my: 4 }} />}
              <EntitiesHandler />
              <Divider sx={{ my: 4 }} />
              <IntentsHandler />
              <Divider sx={{ my: 4 }} />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
}
